import React, { useEffect, useState } from 'react';
import { CheckOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { transformToTitleCase } from '@utils/string';
import { IPipelineTemplate, ITag } from '@redux/types/types';
import {
  PIPELINE_TEMPLATE_LANGUAGES_CODES,
  PipelineTemplateMetadataFields,
} from '@modules/PipelineTemplates/constants/pipeline-templates';
import PipelineTemplatesTags from './PipelineTemplatesTags';
import styles from './pipelineTemplateMetadata.module.scss';

interface IPipelineTemplateMetadataProps {
  template: IPipelineTemplate | null;
  vertical?: boolean;
}

const enum ExcludedTags {
  LANGUAGE = 'language:',
  MULTILINGUAL = 'multilingual',
}

const PipelineTemplateMetadata = (props: IPipelineTemplateMetadataProps) => {
  const { template, vertical = false } = props;
  const [languages, setLanguages] = useState<ITag[]>([]);

  useEffect(() => {
    if (template) {
      template.tags.forEach((tag) => {
        if (
          tag.name.includes(ExcludedTags.LANGUAGE) &&
          !tag.name.includes(ExcludedTags.MULTILINGUAL)
        ) {
          const newLanguage = {
            ...tag,
            name: tag.name.replace(ExcludedTags.LANGUAGE, ''),
          };
          setLanguages((prevLanguages) => [...prevLanguages, newLanguage]);
        }
      });
    }

    return () => {
      setLanguages([]);
    };
  }, [template]);

  const renderListItem = (
    title: string,
    items?: string[],
    icon?: React.ReactNode,
    isList?: boolean,
  ) => {
    if (!items || !items.length) return null;

    return (
      <div className={styles.metadata_item}>
        <div className={styles.metadata_item_title}>{title}</div>
        <div className={styles.metadata_item_list}>
          {items.map((item) => (
            <div key={item} className={styles.metadata_item_list_item}>
              {icon && icon}
              {isList ? <li>{item}</li> : <div>{item}</div>}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTags = () => {
    if (vertical || !template?.tags?.length) return null;

    return (
      <div className={styles.metadata_item}>
        <div className={styles.metadata_item_title}>{PipelineTemplateMetadataFields.TAGS}</div>
        <PipelineTemplatesTags tags={template.tags} />
      </div>
    );
  };

  const renderLanguages = () => {
    if (!languages.length) return null;

    return (
      <div className={styles.metadata_item}>
        <div className={styles.metadata_item_title}>{PipelineTemplateMetadataFields.LANGUAGES}</div>
        <div className={styles.metadata_item_flags}>
          {languages.map((language) => (
            <div key={language.tag_id}>
              <Tooltip title={transformToTitleCase(language.name)}>
                {
                  PIPELINE_TEMPLATE_LANGUAGES_CODES[
                    language.name as keyof typeof PIPELINE_TEMPLATE_LANGUAGES_CODES
                  ]
                }
              </Tooltip>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (!template) return null;

    if (vertical)
      // TODO(INDEXES): due to some weird reason, PipelineTemplateMetadataFields.RECOMMENDED_DATASET maps to
      // the string 'Expected output' while PipelineTemplateMetadataFields.EXPECTED_OUTPUT maps to
      // 'Prerequisites'. Swapped here so that we don't break the default behavior.
      return (
        <div className={styles.metadata_vertical}>
          {renderListItem(
            PipelineTemplateMetadataFields.BEST_FOR,
            template?.best_for,
            <CheckOutlined />,
          )}
          {renderListItem(
            PipelineTemplateMetadataFields.RECOMMENDED_DATASET,
            template?.expected_output,
          )}
          {renderListItem(
            PipelineTemplateMetadataFields.EXPECTED_OUTPUT,
            template?.recommended_dataset,
            <CheckOutlined />,
          )}
          {renderListItem(
            PipelineTemplateMetadataFields.POTENTIAL_APPLICATIONS,
            template?.potential_applications,
            undefined,
            true,
          )}
          {renderLanguages()}
        </div>
      );

    return (
      <div className={styles.metadata}>
        {renderListItem(
          PipelineTemplateMetadataFields.BEST_FOR,
          template?.best_for,
          <CheckOutlined />,
        )}
        {renderListItem(
          PipelineTemplateMetadataFields.POTENTIAL_APPLICATIONS,
          template?.potential_applications,
          undefined,
          true,
        )}
        {renderListItem(PipelineTemplateMetadataFields.EXPECTED_OUTPUT, template?.expected_output)}
        {renderListItem(
          PipelineTemplateMetadataFields.RECOMMENDED_DATASET,
          template?.recommended_dataset,
          <CheckOutlined />,
        )}
        {renderTags()}
        {renderLanguages()}
      </div>
    );
  };

  return <>{renderContent()}</>;
};

export default PipelineTemplateMetadata;
